import "./styles.css";
import { createPortal } from "react-dom";
import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { useEffect, useLayoutEffect, useRef, useState } from "react";

export interface SortCategoriesPopupDetails {
    onSuccess: () => void;
}

interface Props {
    onClose: () => void;
    details?: SortCategoriesPopupDetails;
}

interface Category {
    _id: string;
    name: string;
    picture: string | null;
    sort: number;
}

interface GhostInfo {
    category: Category;
    startY: number;
    x: number;
    width: number;
    height: number;
}

function DragHandle() {
    return (
        <svg width="12" height="16" viewBox="0 0 12 16" fill="none">
            <circle cx="4" cy="3" r="1.5" fill="currentColor" />
            <circle cx="8" cy="3" r="1.5" fill="currentColor" />
            <circle cx="4" cy="8" r="1.5" fill="currentColor" />
            <circle cx="8" cy="8" r="1.5" fill="currentColor" />
            <circle cx="4" cy="13" r="1.5" fill="currentColor" />
            <circle cx="8" cy="13" r="1.5" fill="currentColor" />
        </svg>
    );
}

export default function SortCategoriesPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const c = t.categories;
    const dir = lang === "ar" ? "rtl" : "ltr";

    // ── Fetch all categories ──────────────────────────────────────────────────
    const { data, loading } = useFetch("products/categories", { query: { limit: 1000 } });

    const [items, setItems] = useState<Category[]>([]);
    const originalRef = useRef<Category[]>([]);

    useEffect(() => {
        const cats: Category[] = data?.data?.categories ?? [];
        const sorted = [...cats].sort((a, b) => a.sort - b.sort);
        setItems(sorted);
        originalRef.current = sorted;
    }, [data]);

    // ── FLIP animation ────────────────────────────────────────────────────────
    // Maps each category _id → its <li> DOM element so we can measure and
    // animate it directly without querying the DOM on every move.
    const itemElemsRef = useRef<Map<string, HTMLLIElement>>(new Map());
    // Positions captured before a reorder so we can compute the delta after.
    const prevPositionsRef = useRef<Map<string, number>>(new Map());
    // Guard: only run FLIP when we triggered a reorder (not on initial load).
    const needsFlipRef = useRef(false);

    const snapshotPositions = () => {
        const map = new Map<string, number>();
        itemElemsRef.current.forEach((el, id) => {
            map.set(id, el.getBoundingClientRect().top);
        });
        prevPositionsRef.current = map;
        needsFlipRef.current = true;
    };

    // Runs synchronously after React updates the DOM but before the browser
    // paints — exactly the right moment to apply FLIP.
    useLayoutEffect(() => {
        if (!needsFlipRef.current) return;
        needsFlipRef.current = false;

        // Skip the placeholder (the item being held) — it's invisible anyway.
        const draggingItemId = dragRef.current !== null ? items[dragRef.current.index]?._id : null;

        itemElemsRef.current.forEach((el, id) => {
            if (id === draggingItemId) return;

            const prevTop = prevPositionsRef.current.get(id);
            if (prevTop === undefined) return;

            const newTop = el.getBoundingClientRect().top;
            const delta = prevTop - newTop;
            if (Math.abs(delta) < 1) return;

            // Invert: snap the element back to where it visually was.
            el.style.transition = "none";
            el.style.transform = `translateY(${delta}px)`;

            // Force the browser to acknowledge the snapped state before we play.
            el.offsetHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions

            // Play: let CSS transition carry it to its true (transform: "") position.
            el.style.transition = "transform 0.18s cubic-bezier(0.25, 0.46, 0.45, 0.94)";
            el.style.transform = "";
        });
    }, [items]);

    // ── Drag state ────────────────────────────────────────────────────────────
    const dragRef = useRef<{
        index: number;
        offsetY: number;
        pointerId: number;
    } | null>(null);

    const [draggingId, setDraggingId] = useState<string | null>(null);
    const [ghostInfo, setGhostInfo] = useState<GhostInfo | null>(null);
    const ghostRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLUListElement>(null);

    const onHandlePointerDown = (e: React.PointerEvent<HTMLElement>, index: number) => {
        e.preventDefault();
        const li = (e.currentTarget as HTMLElement).closest<HTMLLIElement>("li");
        if (!li || !listRef.current) return;

        const rect = li.getBoundingClientRect();

        // eslint-disable-next-line react-hooks/immutability
        dragRef.current = {
            index,
            offsetY: e.clientY - rect.top,
            pointerId: e.pointerId,
        };

        listRef.current.setPointerCapture(e.pointerId);
        setDraggingId(items[index]._id);
        setGhostInfo({
            category: items[index],
            startY: rect.top,
            x: rect.left,
            width: rect.width,
            height: rect.height,
        });
    };

    const onListPointerMove = (e: React.PointerEvent<HTMLUListElement>) => {
        const drag = dragRef.current;
        if (!drag || !listRef.current) return;

        // Ghost follows cursor via direct DOM — zero React re-renders.
        if (ghostRef.current) {
            ghostRef.current.style.top = `${e.clientY - drag.offsetY}px`;
        }

        // Find which slot the ghost is hovering over.
        const lis = listRef.current.querySelectorAll<HTMLLIElement>(":scope > li");
        let newIndex = lis.length - 1;
        for (let i = 0; i < lis.length; i++) {
            const r = lis[i].getBoundingClientRect();
            if (e.clientY < r.top + r.height / 2) {
                newIndex = i;
                break;
            }
        }

        if (newIndex !== drag.index) {
            // Snapshot BEFORE the re-render so FLIP has accurate "First" positions.
            snapshotPositions();
            const from = drag.index;
            setItems((prev) => {
                const arr = [...prev];
                const [moved] = arr.splice(from, 1);
                arr.splice(newIndex, 0, moved);
                return arr;
            });
            // eslint-disable-next-line react-hooks/immutability
            drag.index = newIndex;
        }
    };

    const stopDrag = () => {
        if (dragRef.current && listRef.current) {
            try {
                listRef.current.releasePointerCapture(dragRef.current.pointerId);
            } catch {
                // pointer may already be released
            }
        }
        // eslint-disable-next-line react-hooks/immutability
        dragRef.current = null;
        setDraggingId(null);
        setGhostInfo(null);
    };

    // ── Submit ────────────────────────────────────────────────────────────────
    const { loading: submitting, status: submitStatus, invoke: submitInvoke } = useFetch("products/categories/order", { method: "PUT" }, false);

    const wasSubmitting = useRef(false);
    useEffect(() => {
        if (submitting) {
            wasSubmitting.current = true;
        } else if (wasSubmitting.current) {
            wasSubmitting.current = false;
            if (submitStatus === 200) {
                details?.onSuccess();
                onClose();
            }
        }
    }, [submitting]);

    const handleConfirm = () => {
        if (items.length === 0) return;
        submitInvoke({ data: { ids: items.map((cat) => cat._id) } });
    };

    const handleReset = () => {
        snapshotPositions();
        setItems([...originalRef.current]);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="scp-popup" dir={dir}>
            <div className="scp-header">
                <h2 className="scp-title">{c.sortPopupTitle}</h2>
                <button type="button" className="scp-close" onClick={onClose}>
                    ×
                </button>
            </div>

            <div className="scp-body">
                {loading ? (
                    <div className="scp-loader">
                        <span className="scp-loader-spinner" />
                    </div>
                ) : (
                    <>
                        <p className="scp-hint">{c.sortHint}</p>
                        <ul ref={listRef} className="scp-list" onPointerMove={onListPointerMove} onPointerUp={stopDrag} onPointerCancel={stopDrag}>
                            {items.map((item, index) => (
                                <li
                                    key={item._id}
                                    ref={(el) => {
                                        if (el) itemElemsRef.current.set(item._id, el);
                                        else itemElemsRef.current.delete(item._id);
                                    }}
                                    className={`scp-item${draggingId === item._id ? " scp-item--placeholder" : ""}`}
                                >
                                    <div className="scp-item-handle" onPointerDown={(e) => onHandlePointerDown(e, index)}>
                                        <DragHandle />
                                    </div>
                                    <div className="scp-item-img">
                                        {item.picture ? <img src={item.picture} alt={item.name} /> : <div className="scp-item-img-placeholder" />}
                                    </div>
                                    <span className="scp-item-name">{item.name}</span>
                                    <span className="scp-item-order">{index + 1}</span>
                                </li>
                            ))}
                        </ul>
                    </>
                )}
            </div>

            <div className="scp-footer">
                <button type="button" className="scp-btn scp-btn--reset" onClick={handleReset} disabled={submitting || loading}>
                    {c.sortReset}
                </button>
                <button type="button" className="scp-btn scp-btn--cancel" onClick={onClose} disabled={submitting}>
                    {c.sortCancel}
                </button>
                <button
                    type="button"
                    className="scp-btn scp-btn--submit"
                    onClick={handleConfirm}
                    disabled={submitting || loading || items.length === 0}
                >
                    {submitting ? <span className="scp-spinner" /> : c.sortConfirm}
                </button>
            </div>

            {ghostInfo &&
                createPortal(
                    <div
                        ref={ghostRef}
                        className="scp-ghost"
                        style={{
                            position: "fixed",
                            left: ghostInfo.x,
                            top: ghostInfo.startY,
                            width: ghostInfo.width,
                            height: ghostInfo.height,
                            pointerEvents: "none",
                            zIndex: 99999,
                        }}
                    >
                        <div className="scp-ghost-card">
                            <div className="scp-item-handle scp-item-handle--active">
                                <DragHandle />
                            </div>
                            <div className="scp-item-img">
                                {ghostInfo.category.picture ? (
                                    <img src={ghostInfo.category.picture} alt={ghostInfo.category.name} />
                                ) : (
                                    <div className="scp-item-img-placeholder" />
                                )}
                            </div>
                            <span className="scp-item-name">{ghostInfo.category.name}</span>
                        </div>
                    </div>,
                    document.body
                )}
        </div>
    );
}
